/**
 * preflopTable.ts —— 翻牌前 169 类等价手牌的**精确**赢率离线表（PRD 4.5 / AC4.5）
 *
 * 为什么要有这张表：
 *   翻牌前实时精确枚举需要 C(50,5) × C(45,2) = 2,097,572,400 次 7 张牌评估，
 *   即使 3e8 evals/s 也要约 7 秒 —— 完全无法满足 PRD 4.6 的响应预算。
 *   而翻牌前的赢率只取决于 169 个起手牌等价类（同构对称），因此可以离线算完。
 *
 * 数据是怎么来的：
 *   scripts/build-preflop-table/gen_preflop.c，OpenMP 全量枚举，耗时 1068 秒。
 *   零抽样、零近似，输出整数计数。已通过两级独立交叉校验：
 *     V1 穷举 C(52,7)=133,784,560 手，位运算评估器 vs 朴素 21 组合评估器，0 处不一致；
 *     V2 换向枚举（对手外层/牌面内层）复算 8 个等价类，L/T/W 逐位相同。
 *   详见 docs/dev-selftest-core.md。
 *
 * ⚠ 术语（全引擎统一，勿改）：
 *   win  = 我击败对手的组合数
 *   tie  = 与对手平分的组合数
 *   lose = 我被击败的组合数 = denominator - win - tie
 *   资产里**只存 win 与 tie**，lose 由分母推导，从结构上排除了「三个数互相矛盾」的故障。
 */

import { allHandClassLabels, handClassLabel, type Card } from './cards';
import asset from './data/preflop.json';

/** C(50,5) —— 翻牌前完整牌面总数。 */
export const PREFLOP_BOARDS = 2118760;
/** C(45,2) —— 单牌面下对手两张牌组合数。 */
export const PREFLOP_OPPONENT_COMBOS = 990;
/** 翻牌前精确分母 = 2,097,572,400。 */
export const PREFLOP_DENOMINATOR = PREFLOP_BOARDS * PREFLOP_OPPONENT_COMBOS;
/** 起手牌等价类总数。 */
export const HAND_CLASS_COUNT = 169;

/** 单个等价类的精确赢率条目。 */
export interface PreflopEntry {
  /** 等价类标签，如 'AA' / 'AKs' / '72o'。 */
  readonly label: string;
  /** 我击败一名随机对手的概率。 */
  readonly win: number;
  /** 与一名随机对手打平的概率。 */
  readonly tie: number;
  /** 被一名随机对手击败的概率。 */
  readonly lose: number;
  /** 底池份额期望 = win + tie / 2。 */
  readonly equity: number;
  /** 精确胜出组合数（整数）。 */
  readonly w: number;
  /** 精确平分组合数（整数）。 */
  readonly t: number;
  /** 精确落败组合数（整数）。 */
  readonly l: number;
  /** 恒为 PREFLOP_DENOMINATOR。 */
  readonly denominator: number;
}

/** 离线表可用性状态。UI 据此决定展示数值还是「暂不可用」（AC4.5）。 */
export interface PreflopTableStatus {
  readonly available: boolean;
  /** 不可用原因（人类可读，中文）；可用时为 null。 */
  readonly reason: string | null;
  readonly schemaVersion: number;
  readonly classes: number;
  readonly checksum: string;
  /** 运行时复算出的 checksum，用于排查资产被篡改/截断。 */
  readonly computedChecksum: string;
}

/* ------------------------------------------------------------------ *
 * 资产加载与自检
 * ------------------------------------------------------------------ */

/** FNV-1a 32 位。必须与 scripts/build-preflop-table/run.mjs 的实现逐字节一致。 */
function fnv1a32(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

interface RawAsset {
  schemaVersion: number;
  method: string;
  sampling: boolean;
  boards: number;
  opponentCombos: number;
  denominator: number;
  classes: number;
  checksum: string;
  labels: string[];
  wt: number[];
}

/**
 * 模块加载时做一次完整自检。
 *
 * 这里刻意**不 throw** —— 契约要求「表不可用时 UI 展示暂不可用，而不是崩溃或显示
 * 一个错误数字」。因此自检失败只把 available 置为 false，由 equity.ts 在真正需要
 * 翻牌前数值时抛 PreflopTableUnavailableError。
 */
function load(): { entries: readonly PreflopEntry[]; byLabel: ReadonlyMap<string, PreflopEntry>; status: PreflopTableStatus } {
  const a = asset as unknown as RawAsset;
  const problems: string[] = [];
  let computedChecksum = '';

  const emptyStatus = (reason: string): PreflopTableStatus => ({
    available: false,
    reason,
    schemaVersion: a?.schemaVersion ?? -1,
    classes: a?.labels?.length ?? 0,
    checksum: a?.checksum ?? '',
    computedChecksum,
  });

  if (!a || typeof a !== 'object') {
    return { entries: [], byLabel: new Map(), status: emptyStatus('资产缺失或不是对象') };
  }
  if (a.schemaVersion !== 1) problems.push(`schemaVersion 期望 1，实际 ${a.schemaVersion}`);
  if (a.sampling !== false) problems.push('sampling 必须为 false —— 本引擎不接受抽样近似');
  if (a.method !== 'exhaustive-enumeration') problems.push(`method 期望 exhaustive-enumeration，实际 ${a.method}`);
  if (a.boards !== PREFLOP_BOARDS) problems.push(`boards 期望 ${PREFLOP_BOARDS}，实际 ${a.boards}`);
  if (a.opponentCombos !== PREFLOP_OPPONENT_COMBOS) problems.push(`opponentCombos 期望 ${PREFLOP_OPPONENT_COMBOS}，实际 ${a.opponentCombos}`);
  if (a.denominator !== PREFLOP_DENOMINATOR) problems.push(`denominator 期望 ${PREFLOP_DENOMINATOR}，实际 ${a.denominator}`);
  if (!Array.isArray(a.labels) || a.labels.length !== HAND_CLASS_COUNT) {
    problems.push(`labels 期望 ${HAND_CLASS_COUNT} 项，实际 ${a.labels?.length}`);
  }
  if (!Array.isArray(a.wt) || a.wt.length !== HAND_CLASS_COUNT * 2) {
    problems.push(`wt 期望 ${HAND_CLASS_COUNT * 2} 项，实际 ${a.wt?.length}`);
  }

  if (problems.length) {
    return { entries: [], byLabel: new Map(), status: emptyStatus(problems.join('；')) };
  }

  // checksum 复算：防止资产被误改/截断后静默使用错误数值
  computedChecksum = fnv1a32(a.labels.join(',') + '|' + a.wt.join(',')).toString(16).padStart(8, '0');
  if (computedChecksum !== a.checksum) {
    problems.push(`checksum 不匹配：资产声明 ${a.checksum}，实际复算 ${computedChecksum}`);
  }

  // 标签顺序必须与 cards.ts 的规范顺序完全一致（两处独立实现，互为校验）
  const canonical = allHandClassLabels();
  for (let i = 0; i < HAND_CLASS_COUNT; i++) {
    if (a.labels[i] !== canonical[i]) {
      problems.push(`labels[${i}] 期望 ${canonical[i]}，实际 ${a.labels[i]}`);
      break;
    }
  }

  const entries: PreflopEntry[] = [];
  const byLabel = new Map<string, PreflopEntry>();
  for (let i = 0; i < HAND_CLASS_COUNT && problems.length === 0; i++) {
    const w = a.wt[i * 2];
    const t = a.wt[i * 2 + 1];
    if (!Number.isSafeInteger(w) || !Number.isSafeInteger(t) || w < 0 || t < 0 || w + t > PREFLOP_DENOMINATOR) {
      problems.push(`${a.labels[i]} 的计数非法：win=${w} tie=${t}`);
      break;
    }
    const l = PREFLOP_DENOMINATOR - w - t;
    const entry: PreflopEntry = {
      label: a.labels[i],
      win: w / PREFLOP_DENOMINATOR,
      tie: t / PREFLOP_DENOMINATOR,
      lose: l / PREFLOP_DENOMINATOR,
      equity: (w + t / 2) / PREFLOP_DENOMINATOR,
      w,
      t,
      l,
      denominator: PREFLOP_DENOMINATOR,
    };
    entries.push(entry);
    byLabel.set(entry.label, entry);
  }

  if (problems.length) {
    return { entries: [], byLabel: new Map(), status: emptyStatus(problems.join('；')) };
  }

  return {
    entries,
    byLabel,
    status: {
      available: true,
      reason: null,
      schemaVersion: a.schemaVersion,
      classes: entries.length,
      checksum: a.checksum,
      computedChecksum,
    },
  };
}

const LOADED = load();

/* ------------------------------------------------------------------ *
 * 公开 API
 * ------------------------------------------------------------------ */

/** 离线表是否可用。equity.ts 与 UI 在读取翻牌前数值前必须先检查。 */
export function isPreflopTableAvailable(): boolean {
  return LOADED.status.available;
}

/** 离线表状态详情（含不可用原因），供错误提示与埋点使用。 */
export function preflopTableStatus(): PreflopTableStatus {
  return LOADED.status;
}

/**
 * 按等价类标签查表。
 * @throws 表不可用或标签非法时抛错 —— 调用方应先用 isPreflopTableAvailable() 判断。
 */
export function lookupPreflop(label: string): PreflopEntry {
  const e = LOADED.byLabel.get(label);
  if (!e) {
    if (!LOADED.status.available) throw new Error(`翻牌前离线表不可用：${LOADED.status.reason}`);
    throw new Error(`未知起手牌等价类：${label}`);
  }
  return e;
}

/** 按两张底牌查表（内部先归一为 169 类之一）。 */
export function lookupPreflopByCards(a: Card, b: Card): PreflopEntry {
  return lookupPreflop(handClassLabel(a, b));
}

/** 全部 169 条，按资产中的规范顺序（AA, KK, ..., AKs, ..., 32o）。 */
export function allPreflopEntries(): readonly PreflopEntry[] {
  return LOADED.entries;
}

/**
 * 按 equity 从高到低排序的等价类列表。
 * 用于「起手牌强度榜」与合理性断言（AA 必须第一）。
 */
export function preflopRanking(): readonly PreflopEntry[] {
  return [...LOADED.entries].sort((x, y) => y.equity - x.equity || x.label.localeCompare(y.label));
}
